<template>
    <div>
        <DatePicker
            ref="datePicker"
            :disabled="disabled"
            v-model="value"
            range
            class="date-picker"
            type="date"
            range-separator=" - "
            format="DD.MM.YY"
            :clearable="false"
            :lang="options.lang"
            :confirm="true"
            :editable="false"
            :disabled-date="disabledDate"
            @pick="change"
            @close="close"
        >
            <template #sidebar>
                <ul class="btn-list">
                    <li
                        v-for="(item, ind) of options.namesArr"
                        :class="{'active': item.date === parseDateToString(value)}"
                        :key="ind"
                        @click="item.onClick(ind)"
                        :data-date="item.date"
                    >{{ item.text }}
                    </li>
                </ul>
            </template>

            <template #footer>
                <vs-button
                    class="m-2"
                    color="danger"
                    @click="cancel"
                    type="flat"
                >
                    {{ $t('calendar.Cancel') }}
                </vs-button>
                <vs-button
                    class="m-2"
                    color="primary"
                    @click="apply"
                    type="filled"
                >
                    {{ $t('calendar.Apply') }}
                </vs-button>
            </template>
        </DatePicker>
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'

export default {
    props: {
        date: {
            type: Object,
            required: true
        },

        direction: {
            type: String,
            required: true
        },

        disabled: {
            type: Boolean,
            required: false
        }
    },

    data() {
        return {
            disableDates: false,
            value: [new Date(), new Date()],
            applyValue: [new Date(), new Date()],
            options: {
                namesArr: [],
                lang: {
                    formatLocale: {
                        monthsShort: [],
                        weekdaysMin: []
                    }
                }
            }
        }
    },

    components: {DatePicker},

    watch: {
        date: {
            handler(val) {
                this.value = this.parseDate(val)
            },
            deep: true
        },

        '$i18n.locale'() {
            this.monthsShort()
            this.weekdaysMin()
            this.sidebar()
        }
    },

    methods: {
        change() {
            this.disableDates = !this.disableDates
        },

        close() {
            this.disableDates = false
            this.cancel()
        },

        disabledDate(date, currentValue) {
            if(this.disableDates) {
                let a = new Date(currentValue[0])
                return date < a.setMonth(a.getMonth() - 3) || date > a.setMonth(a.getMonth() + 6)
            }

        },

        parseDate(date) {
            const {
                startDate,
                endDate
            } = date
            return [new Date(startDate), new Date(endDate)]
        },

        parseDateToString(date) {
            return `${date[0].toLocaleDateString('ru')}-${date[1].toLocaleDateString('ru')}`
        },

        sidebar() {
            const fns = {
                today: (index) => {
                    const date = new Date()
                    const arr = [date, date]
                    if(index !== undefined) {
                        this.value = arr
                    }
                    return arr
                },

                yesterday: (index) => {
                    const date = new Date()
                    date.setTime(date.getTime() - 3600 * 1000 * 24)
                    const arr = [date, date]
                    if(index !== undefined) {
                        this.value = arr
                    }
                    return arr
                },

                this_month: (index) => {
                    const date = new Date()
                    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)
                    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
                    const arr = [firstDay, lastDay]
                    if(index !== undefined) {
                        this.value = arr
                    }
                    return arr
                },

                last_month: (index) => {
                    const date = new Date()
                    const firstMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1)
                    const lastMonth = new Date(date.getFullYear(), date.getMonth(), 0)
                    const arr = [firstMonth, lastMonth]
                    if(index !== undefined) {
                        this.value = arr
                    }
                    return arr
                }
            }

            const names = [
                'today',
                'yesterday',
                'this_month',
                'last_month'
            ]

            this.options.namesArr = names.map(name => {
                const object = {}
                object.text = this.$t(`calendar.sidebar.${name}`)
                object.action = name
                object.date = this.parseDateToString(fns[name]())
                object.onClick = fns[name]
                return object
            })
        },

        monthsShort() {
            const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            this.options.lang.formatLocale.monthsShort = monthsShort.map(month => this.$t(`calendar.monthNames_short.${month}`))
        },

        weekdaysMin() {
            const weeks = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
            this.options.lang.formatLocale.weekdaysMin = weeks.map(week => this.$t(`calendar.weekdaysMin.${week}`))
        },

        cancel() {
            this.value = this.applyValue
            this.$refs.datePicker.closePopup()
        },

        apply() {
            let data = this.$refs.datePicker.currentValue

            data = {
                startDate: data[0],
                endDate: data[1],
                direction: this.direction
            }

            this.applyValue = this.$refs.datePicker.currentValue
            this.$emit('select', data)
            this.cancel()
        }
    },

    mounted() {
        this.value = this.parseDate(this.date)

        this.monthsShort()
        this.weekdaysMin()
        this.sidebar()
    }
}
</script>

<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';

.date-picker {
    width: 100%;
    background: #fff;
    border-radius: 4px;
    height: 38px;

    .mx-input-wrapper {
        height: 100%;

        input {
            padding: 5px 30px 5px 10px;
            height: 100%;
            box-shadow: none;

            &:focus, &:hover {
                border-color: #ccc !important;
            }
        }
    }
}

.mx-calendar-content {
    height: auto;

    .cell.active {
        background-color: rgba(var(--vs-primary), 1) !important;
        border-radius: 6px;
    }
}

.mx-datepicker-sidebar {
    padding: 0;
}

.btn-list {
    li {
        padding: 8px 12px;
        cursor: pointer;
        font-size: 12px;

        &:hover {
            background-color: #f3f9fe;
        }

        &.active {
            background-color: rgba(var(--vs-primary), 1) !important;
            color: white;
        }
    }
}

.mx-datepicker-btn-confirm {
    display: none;
}

</style>
